import cn from 'classnames'
import * as React from 'preact/compat'

import { disableScroll, enableScroll, loadStylesheet } from '#js/common/helpers'

import SVG from './SVG'

import alertCss from '#css/sg-alert.pcss.json'

interface ModalProps {
  showClose?: boolean
  onClose?: () => void
  mobileFull?: boolean
  mobileFullScreen?: boolean
  disableOutsideClose?: boolean
  child?: HTMLElement,
  enableScroll?: boolean
  oldModal?: boolean,
  style?: React.JSX.CSSProperties | string,
  additionalClass?: string,
}

interface ModalState {
  shown: boolean,
  completelyUnmounted: boolean,
}

let modalCounter = 0

export class Portal extends React.Component<{
  portalId?: string,
  children: any
}, {}> {
  portalElement: HTMLElement | null = null

  componentDidMount() {
    let p = this.props.portalId && document.getElementById(this.props.portalId)
    if (!p) {
      p = document.createElement('div')
      p.id = this.props.portalId ?? 'sg-modal-portal'
      document.body.appendChild(p)
    }
    this.portalElement = p
    this.forceUpdate()
  }

  componentWillUnmount() {
    if (this.portalElement) {
      this.portalElement.remove()
    }
  }

  render() {
    if (!this.portalElement) {
      return null
    }
    return React.createPortal(this.props.children, this.portalElement)
  }
}

export default class Modal extends React.Component<ModalProps, ModalState> {
  state: ModalState = {
    shown: false,
    completelyUnmounted: false
  }

  id = modalCounter
  disabledScroll = false
  dialog = React.createRef<HTMLDialogElement>()
  portal = React.createRef<Portal>()
  inner = React.createRef<HTMLDivElement>()
  closeTriggered = false

  handleCloseClick = (_event: React.JSX.TargetedMouseEvent<HTMLButtonElement>) => {
    // noinspection JSIgnoredPromiseFromCall
    this.close()
  }

  close = () => new Promise<void>((resolve) => {
    if (this.closeTriggered) {
      return;
    }
    this.closeTriggered = true
    this.setState({
      shown: false
    })
    setTimeout(() => {
      this.setState({
        completelyUnmounted: true
      }, () => {
        this.dialog.current?.close()
        if (typeof this.props.onClose === 'function') {
          this.props.onClose()
        }
        resolve()
        const event = new CustomEvent('modalClosed', { detail: { modalId: this.id } });
        window.dispatchEvent(event);
      })
    }, 300)
  })

  dialogClose = (event: Event) => {
    event.preventDefault()
    // noinspection JSIgnoredPromiseFromCall
    this.close()
  }

  preventBubbling = (e: MouseEvent) => {
    e.stopPropagation();
  }

  handleCloseAllModals = (event: CustomEvent<{ except: string }>) => {
    if (parseInt(event.detail?.except) != this.id) {
      // noinspection JSIgnoredPromiseFromCall
      this.close()
    }
  }

  componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('sg-alert')
    modalCounter++
    if (!this.props.enableScroll) {
      this.disabledScroll = disableScroll()
    }
    window.addEventListener('close-all-modals', this.handleCloseAllModals)
    if (this.props.disableOutsideClose) {
      document.addEventListener('keydown', this.disableEscClose)
    }
  }

  componentDidUpdate(prevProps: ModalProps) {
    if (prevProps.disableOutsideClose !== this.props.disableOutsideClose) {
      if (this.props.disableOutsideClose) {
        document.addEventListener('keydown', this.disableEscClose)
      } else {
        document.removeEventListener('keydown', this.disableEscClose)
      }
    }
  }

  componentWillUnmount() {
    if (!this.props.enableScroll && this.disabledScroll) {
      enableScroll()
    }
    if (this.props.disableOutsideClose) {
      document.removeEventListener('keydown', this.disableEscClose)
    }
    window.removeEventListener('close-all-modals', this.handleCloseAllModals)
  }

  disableEscClose = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  public waitForDialog() {
    const dialogMountedCallback = () => {
      this.dialog.current.showModal()
      window.removeEventListener(`dialog-${modalCounter}-mounted`, dialogMountedCallback)
    }

    window.addEventListener(`dialog-${modalCounter}-mounted`, dialogMountedCallback)
  }

  public componentDidMount() {
    if (!this.dialog.current) {
      this.waitForDialog()
    } else {
      this.dialog.current?.showModal()
    }

    setTimeout(() => {
      if (this.dialog.current || this.portal.current) {
        this.setState({
          shown: true
        })
      }
    }, 300)
  }

  overlayClick = () => {
    if (this.props.disableOutsideClose) {
      return
    }
    // noinspection JSIgnoredPromiseFromCall
    this.close()
  }

  setDialog = (node: HTMLDialogElement) => {
    this.dialog.current = node
    window.dispatchEvent(new Event(`dialog-${modalCounter}-mounted`))
  }

  dialogClick = (event: React.TargetedEvent<HTMLElement>) => {
    if (this.props.disableOutsideClose) {
      return
    }
    if (this.inner.current?.contains(event.target as Node)) {
      return
    }
    // noinspection JSIgnoredPromiseFromCall
    this.close()
  }

  render() {
    const { showClose, mobileFull, mobileFullScreen, oldModal } = this.props
    const { shown, completelyUnmounted } = this.state

    if (completelyUnmounted) {
      return null
    }

    const inner = <div className={alertCss['modal__inner']} ref={this.inner}>
      {this.props.children}
      {this.props.child && <div
        ref={ref => {
          if (ref) {
            ref.appendChild(this.props.child)
          }
        }}
      />}
      {showClose &&
        <button type="button" className={cn(alertCss['close'], 'sg-modal-close')} onClick={this.handleCloseClick}>
          <SVG i='sg-new/cross' adaptive/>
        </button>
      }
    </div>

    if (typeof HTMLDialogElement === 'function' && !oldModal) {
      return <Portal portalId={`modal-portal-${modalCounter}`} ref={this.portal}>
        <dialog
          ref={this.setDialog}
          className={cn({
            [alertCss['modal']]: true,
            [alertCss['modal--in']]: shown,
            [alertCss['modal--mobile-full']]: mobileFull || mobileFullScreen,
            [alertCss['modal--mobile-full-screen']]: mobileFullScreen,
            [this.props.additionalClass ?? '']: this.props.additionalClass !== undefined,
          })}
          //@ts-ignore
          onClose={this.dialogClose}
          onClick={this.dialogClick}
          style={this.props.style ?? ''}
        >
          {inner}
        </dialog>
      </Portal>
    }

    return <Portal portalId={`modal-portal-${modalCounter}`} ref={this.portal}>
      <div
        className={cn(alertCss['overlay'], {
          [alertCss['overlay--in']]: shown,
        })}
        onClick={this.overlayClick}
      >
        <div
          className={cn({
            [alertCss['modal']]: true,
            [alertCss['modal--in']]: shown,
            [alertCss['modal--mobile-full']]: mobileFull || mobileFullScreen,
            [alertCss['modal--mobile-full-screen']]: mobileFullScreen,
            [this.props.additionalClass ?? '']: this.props.additionalClass !== undefined,
          })}
          onClick={this.preventBubbling}
        >
          {inner}
        </div>
      </div>
    </Portal>
  }
}
